import { useState } from "react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Layout, Check, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

// Vues prédéfinies : chaque vue = liste ordonnée d'IDs de colonnes visibles
export const BUILT_IN_VIEWS = [
  {
    id: "default",
    name: "Vue par défaut",
    description: "Vue standard optimisée (12 colonnes)",
    columnIds: ["societe","type","etat","status","produit","abonnement_gi","mrr","vendeur","revendeur","leaser","date_fin","ca_leaser"],
    builtIn: true,
  },
  {
    id: "comptable",
    name: "Vue Comptable",
    description: "Focus finances & MRR",
    columnIds: ["societe","type","status","abonnement_gi","abonnement_fin","mrr","ca_leaser","date_fin"],
    builtIn: true,
  },
  {
    id: "commerciale",
    name: "Vue Commerciale",
    description: "Focus vendeur, revendeur & signatures",
    columnIds: ["societe","type","status","vendeur","revendeur","produit","mrr","date_installation","date_fin"],
    builtIn: true,
  },
  {
    id: "resiliations",
    name: "Vue Résiliations",
    description: "Suivi des résiliations en cours",
    columnIds: ["societe","type","status","date_demande_resiliation","date_effet_resiliation","abonnement_gi","mrr"],
    builtIn: true,
  },
  {
    id: "financement",
    name: "Vue Financement",
    description: "Leasers & financements",
    columnIds: ["societe","type","leaser","numero_financement","abonnement_fin","ca_leaser","duree_mois","date_fin"],
    builtIn: true,
  },
];

const CUSTOM_VIEWS_STORAGE_KEY = "contracts_custom_views";
const ACTIVE_VIEW_STORAGE_KEY = "contracts_active_view";

export const loadCustomViews = () => {
  try {
    const saved = localStorage.getItem(CUSTOM_VIEWS_STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch (e) { return []; }
};
export const saveCustomViews = (views) => {
  try { localStorage.setItem(CUSTOM_VIEWS_STORAGE_KEY, JSON.stringify(views)); } catch (e) { /* ignore */ }
};
export const loadActiveViewId = () => {
  try { return localStorage.getItem(ACTIVE_VIEW_STORAGE_KEY) || "default"; }
  catch (e) { return "default"; }
};
export const saveActiveViewId = (id) => {
  try { localStorage.setItem(ACTIVE_VIEW_STORAGE_KEY, id); } catch (e) { /* ignore */ }
};

/**
 * Applique une vue au tableau de colonnes existant : pour chaque colonne du modèle,
 * elle devient visible=true si son id est dans view.columnIds, sinon visible=false.
 * L'ordre de la vue est respecté pour les colonnes visibles.
 */
export const applyViewToColumns = (allColumns, view) => {
  const visibleSet = new Set(view.columnIds);
  const visibleOrdered = view.columnIds
    .map(id => allColumns.find(c => c.id === id))
    .filter(Boolean)
    .map(c => ({ ...c, visible: true }));
  const hidden = allColumns
    .filter(c => !visibleSet.has(c.id))
    .map(c => ({ ...c, visible: false }));
  return [...visibleOrdered, ...hidden];
};

export default function ContractViewsSelector({ activeViewId, onSelectView, currentColumns }) {
  const [customViews, setCustomViews] = useState(() => loadCustomViews());
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newViewName, setNewViewName] = useState("");

  const allViews = [...BUILT_IN_VIEWS, ...customViews];
  const activeView = allViews.find(v => v.id === activeViewId) || BUILT_IN_VIEWS[0];

  const handleSaveCustomView = () => {
    const name = newViewName.trim();
    if (!name) {
      toast.error("Nom requis");
      return;
    }
    const id = `custom_${Date.now()}`;
    const visibleColumnIds = currentColumns.filter(c => c.visible).map(c => c.id);
    const newView = {
      id, name,
      description: `${visibleColumnIds.length} colonnes personnalisées`,
      columnIds: visibleColumnIds,
      builtIn: false,
    };
    const updated = [...customViews, newView];
    setCustomViews(updated);
    saveCustomViews(updated);
    saveActiveViewId(id);
    onSelectView(newView);
    toast.success(`Vue "${name}" enregistrée`);
    setSaveDialogOpen(false);
    setNewViewName("");
  };

  const handleDeleteCustomView = (viewId, viewName) => {
    if (!window.confirm(`Supprimer la vue "${viewName}" ?`)) return;
    const updated = customViews.filter(v => v.id !== viewId);
    setCustomViews(updated);
    saveCustomViews(updated);
    if (activeViewId === viewId) {
      onSelectView(BUILT_IN_VIEWS[0]);
    }
    toast.success("Vue supprimée");
  };

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2" data-testid="views-selector-btn">
            <Layout className="w-4 h-4" />
            <span className="hidden sm:inline max-w-[140px] truncate">{activeView.name}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="end">
          <div className="p-3 border-b">
            <h4 className="font-medium text-sm">Vues sauvegardées</h4>
            <p className="text-xs text-muted-foreground mt-0.5">
              Bascule en 1 clic entre différentes configurations de colonnes
            </p>
          </div>

          <div className="max-h-[400px] overflow-y-auto p-2">
            {/* Vues intégrées */}
            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider px-2 py-1">
              Vues intégrées
            </p>
            {BUILT_IN_VIEWS.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => onSelectView(v)}
                className={`w-full flex items-start gap-2 px-2 py-2 rounded hover:bg-blue-50 transition text-left
                  ${activeViewId === v.id ? "bg-blue-50" : ""}
                `}
                data-testid={`view-item-${v.id}`}
              >
                {activeViewId === v.id ? (
                  <Check className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <div className="w-4 h-4 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{v.name}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{v.description}</p>
                </div>
              </button>
            ))}

            {/* Vues personnalisées */}
            {customViews.length > 0 && (
              <>
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider px-2 pt-3 pb-1">
                  Mes vues
                </p>
                {customViews.map((v) => (
                  <div
                    key={v.id}
                    className={`w-full flex items-start gap-2 px-2 py-2 rounded hover:bg-blue-50 transition group
                      ${activeViewId === v.id ? "bg-blue-50" : ""}
                    `}
                  >
                    <button
                      type="button"
                      onClick={() => onSelectView(v)}
                      className="flex items-start gap-2 flex-1 text-left"
                      data-testid={`view-item-${v.id}`}
                    >
                      {activeViewId === v.id ? (
                        <Check className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <div className="w-4 h-4 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{v.name}</p>
                        <p className="text-[11px] text-muted-foreground truncate">{v.description}</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteCustomView(v.id, v.name)}
                      className="opacity-0 group-hover:opacity-70 hover:!opacity-100 hover:text-red-600 transition"
                      title="Supprimer cette vue"
                      data-testid={`delete-view-${v.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </>
            )}
          </div>

          <div className="p-2 border-t">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              onClick={() => setSaveDialogOpen(true)}
              data-testid="save-current-view-btn"
            >
              <Save className="w-4 h-4" />
              Enregistrer la configuration actuelle...
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enregistrer une nouvelle vue</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label htmlFor="view-name">Nom de la vue</Label>
              <Input
                id="view-name"
                value={newViewName}
                onChange={(e) => setNewViewName(e.target.value)}
                placeholder="Ex. Ma vue 2026"
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && handleSaveCustomView()}
                data-testid="new-view-name-input"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Sauvegarde l&apos;ordre et la visibilité actuelle des colonnes ({currentColumns.filter(c => c.visible).length} visibles).
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSaveCustomView} className="gap-2" data-testid="confirm-save-view">
              <Plus className="w-4 h-4" /> Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
